import { useMemo } from 'react';
import {
  availableWidgets,
  narrowWidgets,
  resolveRoleUi,
  type DashboardWidgetKey,
} from '@efacture/core/sector';

import { useAuth } from '@/contexts/AuthContext';
import { roleUiCodes } from '@/lib/role-ui-codes';
import { usePermissions } from '@/hooks/usePermissions';
import { useBillingState } from '@/api/queries/billing';
import { useDashboardWidgetPreference } from '@/api/queries/ui-preferences';
import { useCompanyDisabledWidgets } from './company-disabled-widgets';

export interface DashboardWidgetsView {
  /** False while the plan or the stored choice is still unknown; nothing is fetched until then. */
  ready: boolean;
  /** The widgets to draw, in order. */
  keys: DashboardWidgetKey[];
  /** What the reader may choose among: role, plan, permission and company default applied. */
  options: DashboardWidgetKey[];
  /** Whether the reader has saved a choice of their own. */
  hasOwnChoice: boolean;
}

/**
 * The reader's dashboard: the role's widgets filtered by plan, permission
 * and the modules the company switched off, narrowed and reordered by the company default, then by the
 * reader's own choice (see `resolveDashboardWidgets` in @efacture/core).
 */
export function useDashboardWidgets(): DashboardWidgetsView {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const canReadBilling = hasPermission('billing:read');
  // Without `billing:read` (or on the desktop app) the plan is unknown and
  // gates nothing here; the server gates each block on the tenant's plan.
  const billing = useBillingState({ enabled: canReadBilling });
  const preference = useDashboardWidgetPreference();
  const companyDisabled = useCompanyDisabledWidgets();

  const roleCodes = useMemo(() => roleUiCodes(user?.roles ?? []), [user?.roles]);
  const planFeatures = billing.data?.features ?? null;
  const company = preference.data?.company ?? null;
  const own = preference.data?.user ?? null;

  return useMemo(() => {
    const allowed = availableWidgets({
      roleWidgets: resolveRoleUi(roleCodes).widgets,
      can: hasPermission,
      planFeatures,
      isModuleDisabled: companyDisabled.widget,
    });
    const options = narrowWidgets(allowed, company);
    const keys = narrowWidgets(options, own);
    const ready = !preference.isLoading && !(canReadBilling && billing.isLoading);
    return { ready, keys: ready ? keys : [], options, hasOwnChoice: own !== null };
  }, [
    roleCodes,
    hasPermission,
    planFeatures,
    companyDisabled,
    company,
    own,
    preference.isLoading,
    canReadBilling,
    billing.isLoading,
  ]);
}
